// Live activity card on /agents/:id. Polls /agents/:id/activity every
// 5s. Renders a "RUNNING NOW" pulse when the agent has written to
// its domain tables in the last 2 min, plus a stat row + per-site
// progress table when applicable (Spencer). Per-site rows are
// expandable to show actual keywords + our position + top-3 competitors.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  agentsApi,
  type AgentActivityKeyword,
  type AgentActivityPerSite,
  type AgentActivityResponse,
} from "../lib/api";

export function LiveActivityCard({ agentId }: { agentId: string }) {
  const q = useQuery({
    queryKey: ["agent-activity", agentId],
    queryFn: () => agentsApi.activity(agentId, 30),
    refetchInterval: 5_000,
    enabled: Boolean(agentId),
    staleTime: 4_000,
  });

  if (q.isLoading || !q.data) {
    return (
      <div className="bg-white rounded-xl border border-ink-100 p-5 text-sm text-ink-500">
        Loading live activity…
      </div>
    );
  }
  if (q.error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800">
        Activity probe failed: {String((q.error as Error).message)}
      </div>
    );
  }

  return <Body data={q.data} />;
}

function Body({ data }: { data: AgentActivityResponse }) {
  const lastAt = data.lastActivityAt ? new Date(data.lastActivityAt) : null;
  const sinceSec = lastAt ? Math.round((Date.now() - lastAt.getTime()) / 1000) : null;

  return (
    <div className="bg-white rounded-xl border border-ink-100 p-5 shadow-sm">
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <h2 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700">
          Live activity
        </h2>
        <LivePulse isLive={data.isLive} sinceSec={sinceSec} />
      </div>
      <p className="text-[11px] text-ink-500 mb-3">
        Last {data.windowMin} minutes — auto-refreshes every 5s. Pulls direct from the agent's domain tables (snapshots, alerts) so an in-flight tick is visible before its action log writes.
      </p>

      {data.signals.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          {data.signals.map((s) => (
            <div key={s.label}>
              <div className="text-2xl font-semibold text-ink-900 tabular-nums">{s.value}</div>
              <div className="text-[11px] text-ink-500 uppercase tracking-wide">{s.label}</div>
              {s.hint && <div className="text-[10px] text-ink-400 mt-0.5">{s.hint}</div>}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-ink-500">No activity in the last {data.windowMin} minutes.</p>
      )}

      {data.perSite && data.perSite.length > 0 && (
        <div className="mt-2 border-t border-ink-100 pt-3">
          <div className="text-xs text-ink-500 mb-2">
            Per-site progress — sorted newest first. Click a row to see keywords + competitors probed for that site.
          </div>
          <table className="w-full text-xs">
            <thead className="text-ink-500 uppercase tracking-wider">
              <tr>
                <th className="text-left py-1 w-6"></th>
                <th className="text-left py-1">Site</th>
                <th className="text-right py-1">Keywords</th>
                <th className="text-right py-1">Last write</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {data.perSite.map((p) => <PerSiteRow key={p.siteId} p={p} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PerSiteRow({ p }: { p: AgentActivityPerSite }) {
  const [open, setOpen] = useState(false);
  const expandable = (p.keywords?.length ?? 0) > 0;

  return (
    <>
      <tr
        className={
          (expandable ? "cursor-pointer hover:bg-ink-50/50 " : "") +
          (open ? "bg-ink-50/40" : "")
        }
        onClick={() => expandable && setOpen((o) => !o)}
      >
        <td className="py-1.5 text-ink-400 text-center">
          {expandable ? (open ? "▾" : "▸") : ""}
        </td>
        <td className="py-1.5">
          <Link
            to={`/sites/${p.siteId}`}
            className="text-accent hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {p.domain}
          </Link>
        </td>
        <td className="py-1.5 text-right tabular-nums">{p.keywordsProbed}</td>
        <td className="py-1.5 text-right text-ink-500 tabular-nums">{formatAgo(p.lastWriteAt)}</td>
      </tr>
      {open && p.keywords && (
        <tr className="bg-ink-50/30">
          <td></td>
          <td colSpan={3} className="py-2 pr-2">
            <div className="space-y-1">
              {p.keywords.map((k) => <KeywordLine key={k.keyword} k={k} />)}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function KeywordLine({ k }: { k: AgentActivityKeyword }) {
  const posLabel = k.ourPosition != null ? `#${k.ourPosition}` : "not in top 100";
  const top3 = k.top3.length > 0 ? k.top3.join(" · ") : "—";
  return (
    <div className="grid grid-cols-12 gap-2 text-[11px] py-1 border-b border-ink-100 last:border-0">
      <div className="col-span-4 font-mono text-ink-700 truncate">"{k.keyword}"</div>
      <div className="col-span-2 text-ink-600 tabular-nums">
        we rank {posLabel}
        {k.localPack && <span className="ml-1 text-purple-700">·LP</span>}
      </div>
      <div className="col-span-6 text-ink-500 truncate">top 3: {top3}</div>
    </div>
  );
}

function LivePulse({ isLive, sinceSec }: { isLive: boolean; sinceSec: number | null }) {
  if (sinceSec == null) {
    return <span className="text-xs text-ink-400">No recent activity</span>;
  }
  if (isLive) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
        </span>
        RUNNING — last write {sinceSec}s ago
      </span>
    );
  }
  return (
    <span className="text-xs text-ink-500">
      Idle — last activity {formatAgoSec(sinceSec)}
    </span>
  );
}

function formatAgo(iso: string): string {
  return formatAgoSec(Math.round((Date.now() - new Date(iso).getTime()) / 1000));
}

function formatAgoSec(sec: number): string {
  if (sec < 60) return `${sec}s ago`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}
